import { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import Auth, { SetNewPassword, AccountLoadError } from './Auth';
import Dashboard from './Dashboard';
import Setup from './Setup';
import PublicInvoice from './PublicInvoice';
import PublicContractorInvoice from './PublicContractorInvoice';
import PublicPoRequest from './PublicPoRequest';
import PublicSign from './PublicSign';
import PublicSharedDashboard from './PublicSharedDashboard';
import PlatformAdmin from './PlatformAdmin';
import { OrgProvider, useOrg } from './orgContext';

function Loading() {
  return (
    <div style={{ minHeight:'100vh', background:'#0A1526', display:'flex', alignItems:'center', justifyContent:'center', color:'#64748b', fontFamily:'Inter,sans-serif' }}>
      Loading...
    </div>
  );
}

// Opening the link in a password-reset email lands on the app with type=recovery in the
// URL. Read it at module load, before supabase-js tidies the URL, so the "choose a new
// password" screen shows no matter when the auth event arrives.
const OPENED_FROM_RESET_LINK = /(^|[#&?])type=recovery(&|$)/.test(window.location.hash + window.location.search);

function AppInner() {
  const { org, orgMember, allOrgs, setActiveOrg, loading: orgLoading, reload: reloadOrgs } = useOrg();
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(null);
  const [recovering, setRecovering] = useState(OPENED_FROM_RESET_LINK);
  const [profileFailed, setProfileFailed] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) fetchProfile(session);
      else setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') setRecovering(true);
      setSession(session);
      // A cached true/false from a previous account must never leak into a
      // different account signed into the same tab — reset on every auth
      // transition (sign-in, sign-out, account switch) so the check below
      // always re-runs for whoever is actually signed in now.
      setIsPlatformAdmin(null);
      if (session) fetchProfile(session);
      else { setProfile(null); setLoading(false); }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (session && !orgLoading && !org && isPlatformAdmin === null) {
      supabase.rpc('is_platform_admin').then(({ data, error }) => {
        // A transient error must not be cached as a hard "false" — that would
        // permanently misroute a real platform admin to onboarding for the
        // rest of the tab's session. Leave it null (shows Loading) instead of
        // guessing; a hard refresh or another auth transition will retry.
        if (error) { console.error('is_platform_admin check failed:', error); return; }
        setIsPlatformAdmin(!!data);
      });
    }
  }, [session, orgLoading, org, isPlatformAdmin]);

  const fetchProfile = async (session) => {
    const userId = session.user.id;
    setProfileFailed(false);
    let { data: prof } = await supabase.from('profiles').select('*').eq('id', userId).maybeSingle();

    // OAuth sign-in: create profile if none exists. (handle_new_user normally does this
    // already; this is the safety net. profiles.role was dropped, so it must not be written:
    // the old insert named it, always failed, and left the person on the login screen.)
    if (!prof) {
      const meta = session.user.user_metadata ?? {};
      const name = meta.full_name || meta.name || session.user.email?.split('@')[0] || 'New User';
      const { data: created } = await supabase
        .from('profiles')
        .insert({ id: userId, name })
        .select()
        .single();
      prof = created;
    }

    // Still nothing: say so, instead of dropping back to a login screen that looks like
    // the sign-in did not work.
    if (!prof) { setProfileFailed(true); setLoading(false); return; }
    setProfile(prof);

    // Accept pending invitation — stored either before login (by Auth.js) or
    // when an already-logged-in user clicks an invite link (captured below in App).
    const pendingToken = localStorage.getItem('pendingInviteToken');
    if (pendingToken) {
      const { error } = await supabase.rpc('accept_invitation', { p_token: pendingToken });
      localStorage.removeItem('pendingInviteToken');
      if (!error) await reloadOrgs(userId);
      // On error (expired/invalid): silently clear — user will see their normal dashboard
    }

    setLoading(false);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  if (loading || orgLoading) return <Loading />;

  // Chose "forgot password" and opened the emailed link: let them set the new password
  // before anything else. (They are signed in with a short-lived recovery session.)
  if (session && recovering) {
    return <SetNewPassword onDone={() => setRecovering(false)} onCancel={async () => { setRecovering(false); await handleSignOut(); }} />;
  }

  if (session && !profile && profileFailed) {
    return <AccountLoadError onRetry={() => fetchProfile(session)} onSignOut={handleSignOut} />;
  }

  if (!session || !profile) return <Auth />;

  // No org yet or setup not complete → onboarding wizard.
  // If the user also belongs to a fully set-up org (e.g. they switched into
  // their not-yet-configured personal workspace), give them a way back.
  if (!org || org.setup_complete === false) {
    // A truly org-less account is either a brand-new signup mid-onboarding, or
    // TimeKeep's own platform-admin identity (which never has an org). Resolve
    // which one before falling through to the customer-facing Setup wizard.
    if (!org) {
      if (isPlatformAdmin === null) return <Loading />;
      if (isPlatformAdmin) return <PlatformAdmin session={session} profile={profile} onSignOut={handleSignOut} />;
    }
    const completeOrg = allOrgs.find(m => m.org_id !== org?.id && m.organizations?.setup_complete !== false);
    // Keyed on the org so a wizard that opened before the org arrived can't keep its blank form.
    return <Setup key={org?.id || 'new'} session={session} profile={profile} org={org} onComplete={reloadOrgs} onSignOut={handleSignOut}
      onBack={completeOrg ? () => setActiveOrg(completeOrg.org_id) : null}
      backLabel={completeOrg?.organizations?.name} />;
  }

  return <Dashboard session={session} profile={profile} org={org} orgMember={orgMember} onSignOut={handleSignOut} />;
}

export default function App() {
  // Capture invite token from URL regardless of auth state.
  // Auth.js handles this for unauthenticated users; this covers already-logged-in users
  // who click an invite link and land directly in their dashboard, bypassing Auth.js.
  const inviteParams = new URLSearchParams(window.location.search);
  const inviteToken = inviteParams.get('token') || inviteParams.get('invite');
  if (inviteToken && window.location.pathname === '/accept-invite') {
    localStorage.setItem('pendingInviteToken', inviteToken);
    // Strip the token from the URL so a refresh doesn't re-trigger acceptance
    window.history.replaceState({}, '', '/');
  }

  // Public routes — no auth required
  const publicInvoiceMatch = window.location.pathname.match(/^\/invoice\/([a-f0-9-]{36})$/i);
  if (publicInvoiceMatch) {
    return <PublicInvoice token={publicInvoiceMatch[1]} />;
  }
  const publicCInvoiceMatch = window.location.pathname.match(/^\/cinvoice\/([a-f0-9-]{36})$/i);
  if (publicCInvoiceMatch) {
    return <PublicContractorInvoice token={publicCInvoiceMatch[1]} />;
  }
  const publicPoRequestMatch = window.location.pathname.match(/^\/request-po\/([a-f0-9-]{36})$/i);
  if (publicPoRequestMatch) {
    return <PublicPoRequest token={publicPoRequestMatch[1]} />;
  }
  // Signing tokens are 43 base64url chars (32 bytes), not uuids -- see
  // contractHash.generateSigningToken. Matching the exact shape here means a
  // malformed link never reaches the network.
  const publicSignMatch = window.location.pathname.match(/^\/sign\/([A-Za-z0-9_-]{43})$/);
  if (publicSignMatch) {
    return <PublicSign token={publicSignMatch[1]} />;
  }
  const publicSharedDashboardMatch = window.location.pathname.match(/^\/shared\/dashboard\/([a-f0-9-]{36})$/i);
  if (publicSharedDashboardMatch) {
    return <PublicSharedDashboard token={publicSharedDashboardMatch[1]} />;
  }

  return (
    <OrgProvider>
      <AppInner />
    </OrgProvider>
  );
}
